<template>
  <div
    class="container"
    style="z-index: 100 !important">
    <v-card
      flat
      class="outer subtleshadow pa-3">
      <div class="title-bar mb-2">
        <v-icon class="mr-2">mdi-image-multiple-outline</v-icon>
        {{ props.selected_assets.length }} Assets Selected
        <v-tooltip
          v-if="props.hasNonVisibleSelected"
          location="top">
          <template v-slot:activator="{ props: tooltipProps }">
            <v-icon
              v-bind="tooltipProps"
              color="warning"
              size="small"
              class="ml-2">
              mdi-alert-circle
            </v-icon>
          </template>
          <span>{{ props.nonVisibleCount }} item(s) not currently visible</span>
        </v-tooltip>
      </div>
      <div v-if="mode === 'default'">
        <div class="button-bar">
          <v-btn
            title="Cancel Selection"
            variant="tonal"
            @click="emit('cancelledOrCompleted')">
            <v-icon>mdi-cancel</v-icon>
          </v-btn>
          <v-btn
            title="Group Selected Assets"
            variant="tonal"
            @click="emit('group-requested')">
            <v-icon>mdi-folder-multiple-plus</v-icon>
          </v-btn>
          <v-btn
            title="Delete Selected Assets"
            @click="() => props.multi_delete_func()"
            :loading="props.multi_delete_loading"
            class="delete-btn"
            variant="flat">
            <v-icon color="error">mdi-trash-can</v-icon>
          </v-btn>
          <v-btn
            title="Add Tags to Selected Assets"
            variant="tonal"
            @click="mode = 'addTags'">
            <v-icon>mdi-tag-plus</v-icon>
          </v-btn>
          <v-btn
            title="Remove Tags From Selected Assets"
            variant="tonal"
            @click="mode = 'removeTags'">
            <v-icon>mdi-tag-minus</v-icon>
          </v-btn>
        </div>
      </div>
      <!-- START: Add Tags  -->
      <div v-else-if="mode === 'addTags'">
        <div class="button-bar">
          <v-btn
            title="Back"
            variant="tonal"
            @click="
              mode = 'default';
              selectedTags = [];
            ">
            <v-icon>mdi-arrow-left-bold</v-icon>
          </v-btn>
          <div class="tags">
            <v-combobox
              v-model="selectedTags"
              density="compact"
              clearable
              chips
              multiple
              label="Tags"
              item-value="uid"
              item-title="name"
              :items="tagsArray"></v-combobox>
          </div>
          <v-btn
            :loading="tagsLoading"
            variant="tonal"
            color="primary"
            :disabled="selectedTags.length == 0"
            title="Add these Tags to Selected Assets"
            @click="handleTags('add')">
            <v-icon>mdi-tag-plus</v-icon>
          </v-btn>
        </div>
      </div>
      <!-- END: Add Tags  -->
      <!-- START: Delete Tags  -->
      <div v-else-if="mode === 'removeTags'">
        <div class="button-bar">
          <v-btn
            title="Back"
            variant="tonal"
            @click="
              mode = 'default';
              selectedTags = [];
            ">
            <v-icon>mdi-arrow-left-bold</v-icon>
          </v-btn>
          <div class="tags">
            <v-combobox
              v-model="selectedTags"
              density="compact"
              clearable
              chips
              multiple
              label="Tags"
              item-value="uid"
              item-title="name"
              :items="tagsArray"></v-combobox>
          </div>
          <v-btn
            :loading="tagsLoading"
            variant="tonal"
            color="error"
            :disabled="selectedTags.length == 0"
            title="Remove these Tags from Selected Assets"
            @click="handleTags('delete')">
            <v-icon>mdi-tag-minus</v-icon>
          </v-btn>
        </div>
      </div>
      <!-- END: Delete Tags  -->
    </v-card>
  </div>
</template>
<script setup>
import { deluge } from '@/stores/deluge.js';
import { $http } from '@/stores/http.js';
import { ref, computed } from 'vue';

const mode = ref('default');
const tagsLoading = ref(false);
const selectedTags = ref([]);

const tagsArray = computed(() => Object.values(deluge.tags));

const props = defineProps({
  multi_delete_loading: {
    type: Boolean,
    default: false,
  },
  multi_delete_func: {
    type: Function,
    required: true,
  },
  selected_assets: {
    type: Array,
    default: () => [],
  },
  hasNonVisibleSelected: {
    type: Boolean,
    default: false,
  },
  nonVisibleCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['cancelledOrCompleted', 'group-requested']);

async function handleTags(addOrDelete) {
  tagsLoading.value = true;
  const tagUIDArray = await createTagUidArray();
  const assetUIDs = props.selected_assets;

  const payload = { asset_uids: assetUIDs, tag_uids: tagUIDArray };

  const urls = {
    add: '/bulk_add_tags_to_assets',
    delete: '/bulk_remove_tags_from_assets',
  };

  await $http('PUT', `${urls[addOrDelete]}`, JSON.stringify(payload));
  tagsLoading.value = false;
  selectedTags.value = [];
  emit('cancelledOrCompleted');
}

async function createTagUidArray() {
  const tagUidArray = [];

  for (const tag of selectedTags.value) {
    if (typeof tag === 'string') {
      const resp = await $http(
        'POST',
        '/create_tag',
        JSON.stringify({ name: tag, color: '#5d6ae3' }),
      );
      const created = JSON.parse(resp);
      tagUidArray.push(created.uid);
    } else {
      tagUidArray.push(tag.uid);
    }
  }
  return tagUidArray;
}
</script>
<style scoped>
.container {
  z-index: 2;
  position: fixed;

  bottom: 60px;
  top: auto;
  left: 50%;

  transform: translate(-50%);
}
.outer {
  max-width: 400px;
  min-width: 400px;
  width: 100%;
  height: 100%;
  border-radius: 4px;
}
.title-bar {
  display: flex;
  justify-content: center;
}

.delete-btn {
  background-color: var(--inner-background);
}
.tags {
  max-width: 230px;
  min-width: 230px;
}
.button-bar {
  display: flex;
  gap: 0.6rem;
  justify-content: center;
  justify-content: space-evenly;
}
</style>
